import { catchError, share } from 'rxjs/operators';
import { Observable, AsyncSubject } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { ConfigService } from '../../Core/Config/services/config.service';

type HttpRequestOptions = Parameters<HttpClient['request']>[2];

@Injectable()
export class ServiceApi {
    private static readonly AUTHENTICATION_KEY = 'apiKey';

    public apiUrl: string;

    constructor(
        public httpClient: HttpClient,
        public cookieService: CookieService,
        public configurationService: ConfigService
    ) {
    }

    /**
     * Make an API call to Server
     */
    public api<T>(
        slug: string,
        data: any,
        method: HttpVerbsEnum = HttpVerbsEnum.GET,
        query: object = {},
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text',
        headers?: HttpHeaders,
        version?: string
    ): Observable<T> {
        let queryParams: HttpParams = new HttpParams;

        for (const key in query) {
            if (query.hasOwnProperty(key)) {
                queryParams = queryParams.append(key, query[key]?.toString());
            }
        }

        const apiUrl = version ? this.apiUrl.replace(/\/v[0-9]+/, `/${version}`) : this.apiUrl;
        const url = apiUrl + slug;

        headers = !!headers ? headers : new HttpHeaders();
        if (!headers.get('content-type')) {
            headers = headers.set('content-type', 'application/json');
        }

        if (!responseType || responseType === 'json') {
            headers = headers.set('accept', 'application/json');
        }

        const options: HttpRequestOptions = {
            body: data,
            params: queryParams,
            headers: headers,
            withCredentials: true
        };

        if (responseType) {
            options.responseType = responseType;
        }

        const response = this.httpClient.request(
            method.toString(),
            url,
            options
        ) as any;

        // Prevents sending multiple requests if multiple things need to subscribe to the observable.
        return response.pipe(
            share({
                connector: () => new AsyncSubject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            }),
            catchError(err => {
                // @HACK sometimes the error message doesn't get parsed into JSON properly
                // This makes it so if there is an error, then we will parse it to json if it is possible
                if (err && typeof err.error === 'string') {
                    try {
                        err.error = JSON.parse(err.error);
                    } catch {
                        const jsonStart = err.error.search(/[[{]/);
                        if (jsonStart > -1) {
                            try {
                                err.error = JSON.parse(err.error.slice(jsonStart));
                            } catch {}
                        }
                    }
                }
                // re-throw so it continues down the error path
                throw err;
            })
        );
    }

    /**
     * Make an API call to the server in a streaming fashion
     */
    public streamApi<T>(
        slug: string,
        data: any,
        method: HttpVerbsEnum = HttpVerbsEnum.GET,
        query: object = {},
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text',
        headers?: HttpHeaders
    ): Observable<T> {
        let queryParams: HttpParams = new HttpParams;

        // Adding below chache buster to prevent IE11 caching get call
        if (method === HttpVerbsEnum.GET) {
            queryParams = queryParams.append('buster', new Date().getTime().toString());
        }

        for (const key in query) {
            if (query.hasOwnProperty(key)) {
                queryParams = queryParams.append(key, query[key].toString());
            }
        }

        const url = this.apiUrl + slug;

        headers = !!headers ? headers : new HttpHeaders();
        if (!headers.get('content-type')) {
            headers = headers.set('content-type', 'application/json');
        }

        if (!responseType || responseType === 'json') {
            headers = headers.set('accept', 'application/json');
        }

        const options: HttpRequestOptions = {
            body: data,
            params: queryParams,
            headers: headers,
            withCredentials: true,
            observe: 'body',
            responseType: 'blob',
            reportProgress: true
        };

        if (responseType) {
            options.responseType = responseType;
        }

        return this.httpClient.request(
            method.toString(),
            url,
            options
        ) as any;
    }

    /*
     * Make the API call for getting image blob
     *
     * @param {string} url
     * @returns {Observable<Blob>}
     */
    public getImageApi(
        url: string
    ): Observable<Blob> {

        return  this.httpClient.get(
            url,
            {
                responseType: 'blob'
            },
        );
    }
}
